import { createFileRoute, redirect, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserService } from "@/services";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Logo } from "@/components/core/logo";
import { formatCpfInput, formatDate, formatPhoneInput, toIsoDate, validateAge, validateCPF } from "@/lib/utils";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/base";
import { showToast } from "@/hooks/useToast";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import { useAuth } from "@/contexts";

export const Route = createFileRoute("/auth/complete-profile")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (token && user) {
      throw redirect({
        to: "/app",
      });
    }
  },
  component: RouteComponent,
});

const completeProfileSchema = z
  .object({
    nome: z.string().min(1, "Nome é obrigatório"),
    telefone: z
      .string()
      .min(1, "Telefone é obrigatório")
      .refine((value) => {
        const digits = value.replace(/[^0-9]+/g, "");
        return digits.length === 10 || digits.length === 11;
      }, "Telefone deve ter 10 ou 11 dígitos (com DDD)"),
    cpf: z
      .string()
      .min(1, "CPF é obrigatório")
      .refine(
        (value) => value.replace(/[^0-9]+/g, "").length === 11,
        "CPF deve ter 11 dígitos"
      )
      .refine((value) => validateCPF(value), "CPF inválido"),
    dataNascimento: z.string().min(1, "Data de nascimento é obrigatória"),
    termsAccepted: z.boolean().refine((val) => val === true, {
      message: "Você deve aceitar os termos de uso",
    }),
  })
  .superRefine((data, ctx) => {
    const result = validateAge(data.dataNascimento);
    if (!result.valid && result.age !== undefined) {
      if (result.age < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Você deve ter pelo menos 18 anos",
          path: ["dataNascimento"],
        });
      } else if (result.age > 120) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Data de nascimento inválida",
          path: ["dataNascimento"],
        });
      }
    }
  });

type CompleteProfileSearch = {
  tempToken: string;
  nome: string;
  email: string;
  foto?: string;
};

function RouteComponent() {
  const navigate = useNavigate();
  const { saveAuth } = useAuth();
  const search = useSearch({ from: "/auth/complete-profile" }) as Partial<CompleteProfileSearch>;
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Validar que temos os dados necessários
  if (!search.tempToken || !search.email) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: `url("/authentication-bg.svg")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}>
        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-4xl shadow-xl p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sessão inválida</h2>
            <p className="text-sm text-gray-600 mb-6">Por favor, faça login novamente com Google.</p>
            <Button onClick={() => navigate({ to: "/auth/identify" })}>
              Voltar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const form = useForm<z.infer<typeof completeProfileSchema>>({
    // @ts-ignore
    resolver: zodResolver(completeProfileSchema),
    defaultValues: {
      nome: search.nome || "",
      telefone: "",
      cpf: "",
      dataNascimento: "",
      termsAccepted: false,
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    setIsLoading(true);
    try {
      const sanitizedData = {
        nome: values.nome,
        telefone: values.telefone.replace(/[^0-9]+/g, ""),
        cpf: values.cpf.replace(/[^0-9]+/g, ""),
        dataNascimento: toIsoDate(values.dataNascimento),
      };

      const response = await UserService.completeGoogleProfile(search.tempToken!, sanitizedData);

      // Salva o novo access token e usuário
      saveAuth(response);

      showToast.success("Cadastro completado com sucesso!");
      setSuccess(true);

      setTimeout(() => {
        navigate({
          to: "/auth/verify-phone" as any,
          // @ts-ignore
          search: { telefone: sanitizedData.telefone }
        });
      }, 2000);
    } catch (err) {
      showToast.error(
        err instanceof Error ? err.message : "Não foi possível completar o cadastro"
      );
    } finally {
      setIsLoading(false);
    }
  });

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: `url("/authentication-bg.svg")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}>
        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-4xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Cadastro completado!</h2>
            <p className="text-gray-600">Redirecionando para verificação do telefone...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url("/authentication-bg.svg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}>
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-4xl shadow-xl p-8">
          <div className="flex items-center justify-center mb-6">
            <Logo className="h-12" isTraditionalLogo />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Complete seu cadastro</h1>
            <p className="text-sm text-gray-600">Faltam poucos dados para ativar sua conta</p>
          </div>

          {/* Dados do Google (não editáveis) */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            {search.foto && (
              <div className="flex justify-center mb-4">
                <img
                  src={search.foto}
                  alt={search.nome}
                  className="w-16 h-16 rounded-full object-cover"
                />
              </div>
            )}
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-gray-600">E-mail</p>
                <p className="font-medium text-gray-900">{search.email}</p>
              </div>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="nome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome completo</FormLabel>
                    <FormControl>
                      <Input placeholder="João da Silva" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="telefone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="tel"
                        placeholder="(00) 00000-0000"
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(formatPhoneInput(event.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cpf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPF</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="numeric"
                        placeholder="000.000.000-00"
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(formatCpfInput(event.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dataNascimento"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de nascimento</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="numeric"
                        placeholder="12/11/2002"
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(formatDate(event.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="termsAccepted"
                render={({ field }) => (
                  <FormItem className="flex items-start space-x-3 space-y-0 mt-6">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal cursor-pointer">
                        Aceito os termos de uso e a política de privacidade
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full mt-8" disabled={isLoading}>
                {isLoading ? "Completando cadastro..." : "Completar cadastro"}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate({ to: "/auth/identify" as any })}
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              Mudar de conta? <span className="font-semibold text-blue-600">Fazer login</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
